import { all, takeEvery, put, call, takeLatest } from 'redux-saga/effects';
import moment from "moment/moment";
import omit from 'lodash/omit';
import clone from 'clone';
import { newInvoice } from '../../pages/Invoice/config';
import actions from './actions';
import actionSerie from '@iso/redux/serie/actions';
import actionsLog from '@iso/redux/log/actions';
import { 
  simpleQueryFirestoreWithFilter, 
  simpleQueryFirestoreWithFilterOrderBy,
  paginatedQueryFirestoreWithFilter
} from '../../components/utility/firestore.helper';
import { rsf } from '@iso/lib/firebase/firebase';
import { invoiceSeries, noteSeries } from "@iso/config/site.config";

import { getAccountIdPrefix, getLoggedUserId } from '@iso/components/library/helpers/local_storage';
import DateFormatter from "@iso/components/utility/DateFormatter";

import {updateValues, buildNumberSearchPrefixes, NUMBER_SEARCH_MAX_RESULTS} from "../../pages/Invoice/components/helpers";
import {generateSystemId} from "../../components/library/helpers/idGenerator";
import { isRegistered } from "../../components/library/verifactu/status";
import { REGISTERED_DELETE_MESSAGE } from "../../pages/Invoice/components/verifactu-guards";
import {
  FINAL_LOCK_MESSAGE,
  FINAL_DELETE_MESSAGE,
  isStoredDocEditable,
  isBlockedForFixing,
  invoiceErrorMessage,
} from "../../pages/Invoice/components/invoice-lock";
import { notifyInvoiceError } from "./notify";

const COLLECTION_NAME = 'invoices';
const COLLECTION_NAME_PROP = 'properties';
const COLLECTION_NAME_CLIENT = 'users';

// A client may only open its own, already generated documents. Drafts and other clients' documents
// are treated exactly like a missing id (the page renders empty).
export const CLIENT_VISIBLE_STATUSES = ['invoice', 'note'];

export function isInvoiceVisibleToClient(invoice, clientId) {
  if (!invoice || clientId === null || clientId === undefined || clientId === '') return false;
  return CLIENT_VISIBLE_STATUSES.includes(invoice.orderStatus)
    && String(invoice.billToId) === String(clientId);
}

export function* getInvoice(query) {
  try {
    const filterInvoice = { 'invoice':  {'value':  query.payload.id, 'operator': '==', 'field': 'id' }};
    const data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, filterInvoice);
    const dataSource = [];
    Object.keys(data).map((item, index) => {
      return dataSource.push({
        ...data[item],
        key: item,
      });
    });
    let found = dataSource[0];
    if (localStorage.getItem('id_role') === 'client' && !isInvoiceVisibleToClient(found, getLoggedUserId())) {
      found = undefined;
    }
    yield put(actions.getInvoiceSuccess(found));
  } catch (error) {
    yield put(actions.getInvoiceError(error));
  }
}

// Returns the search term for the "search by number" box, or '' when not searching.
export function getInvoiceNumberTerm(filter) {
  return (filter && filter.number) ? String(filter.number).trim() : '';
}

// Helper function to build filters from payload
export function buildInvoiceFilters(filter) {
  let filters = [];
  const numberTerm = getInvoiceNumberTerm(filter);
  if (filter !== undefined) {
    if (filter.type) {
      filters.push({ field: 'type', operator: '==', value: filter.type });
    }
    if (numberTerm) {
      // Search by invoice number (magnifier). Firestore has no "contains", so we
      // do a prefix match: number >= term AND number <= term + \uf8ff.
      // The date range is deliberately dropped while searching: Firestore only
      // allows range filters on ONE field, and invoice numbers repeat every year
      // (the serie resets), so a lookup must span every year — not just the
      // date range selected in the list (which defaults to the current year).
      filters.push({ field: 'number', operator: '>=', value: numberTerm });
      filters.push({ field: 'number', operator: '<=', value: numberTerm + '\uf8ff' });
    } else {
      if (filter.startDate) {
        filters.push({ field: 'orderDate', operator: '>=', value: filter.startDate.valueOf() });
      }
      if (filter.endDate) {
        filters.push({ field: 'orderDate', operator: '<=', value: filter.endDate.valueOf() });
      }
    }
    if (filter.billToId) {
      filters.push({ field: 'billToId', operator: '==', value: filter.billToId });
    }
    if (filter.idOwner) {
      filters.push({ field: 'idOwner', operator: '==', value: filter.idOwner });
    }
    // The serie is already the first part of the number (1/0426), so it is
    // redundant while searching and would need its own composite index.
    if (filter.invoice_serie && !numberTerm) {
      filters.push({ field: 'invoice_serie', operator: '==', value: filter.invoice_serie });
    }
    if (filter.orderStatus) {
      filters.push({ field: 'orderStatus', operator: '==', value: filter.orderStatus });
    }
  }
  filters.push({ field: 'deleted_at', operator: '==', value: null });
  return filters;
}

// Firestore requires the first orderBy to be the field carrying the range
// filter, so ordering switches to `number` while searching by number.
export function buildInvoiceOrderBy(filter) {
  return getInvoiceNumberTerm(filter)
    ? { field: "number", type: "asc" }
    : { field: "orderDate", type: "desc" };
}

// Equality-only filters shared by every prefix query of a number search
// (no date range, no serie — see buildInvoiceFilters for the why).
function buildNumberSearchBaseFilters(filter) {
  const filters = [];
  if (filter) {
    if (filter.type) filters.push({ field: 'type', operator: '==', value: filter.type });
    if (filter.billToId) filters.push({ field: 'billToId', operator: '==', value: filter.billToId });
    if (filter.idOwner) filters.push({ field: 'idOwner', operator: '==', value: filter.idOwner });
    if (filter.orderStatus) filters.push({ field: 'orderStatus', operator: '==', value: filter.orderStatus });
  }
  filters.push({ field: 'deleted_at', operator: '==', value: null });
  return filters;
}

// Resolved lazily and defensively: reading the config at import time crashes
// as soon as it is mocked or reshaped.
function getSerieIds() {
  const ids = [];
  [invoiceSeries, noteSeries].forEach((group) => {
    Object.keys(group || {}).forEach((key) => {
      const id = group[key] && group[key].id;
      if (id !== undefined && id !== null) ids.push(String(id));
    });
  });
  return ids;
}

/**
 * Smart search by invoice number: a partial number is expanded into every
 * plausible prefix and the results of all of them are merged, deduplicated and
 * sorted newest-first. Returned as a single page (searches are lookups, not
 * browsing), so the caller does not paginate with cursors.
 */
export function* searchInvoicesByNumber(filter, pageSize) {
  const term = getInvoiceNumberTerm(filter);
  const prefixes = buildNumberSearchPrefixes(term, getSerieIds());
  const baseFilters = buildNumberSearchBaseFilters(filter);
  const perQueryLimit = Math.max(pageSize, 50);

  const responses = yield all(
    prefixes.map((prefix) =>
      call(
        paginatedQueryFirestoreWithFilter,
        getAccountIdPrefix() + COLLECTION_NAME,
        [
          ...baseFilters,
          { field: 'number', operator: '>=', value: prefix },
          { field: 'number', operator: '<=', value: prefix + '\uf8ff' },
        ],
        { field: 'number', type: 'asc' },
        perQueryLimit,
        null
      )
    )
  );

  const byId = new Map();
  responses.forEach((result) => {
    Object.keys((result && result.data) || {}).forEach((id) => {
      if (!byId.has(id)) byId.set(id, { ...result.data[id], key: id });
    });
  });

  return Array.from(byId.values())
    .sort((a, b) => (b.orderDate || 0) - (a.orderDate || 0))
    .slice(0, NUMBER_SEARCH_MAX_RESULTS);
}

export function* getInvoices({ payload }) {
  try {
    const {filter} = payload;
    let filters = buildInvoiceFilters(filter);
    let orderBy = buildInvoiceOrderBy(filter);
    let data = yield simpleQueryFirestoreWithFilterOrderBy(getAccountIdPrefix() + COLLECTION_NAME, filters, orderBy);
    const dataSource = [];
    Object.keys(data).map((item, index) => {
      return dataSource.push({
        ...data[item],
        key: item,
      });
    });
    yield put({
      type: actions.GET_INVOICES_SUCCESS,
      invoices: dataSource
    });
  } catch (error) {
    console.log(error);
    yield put({
      type: actions.GET_INVOICES_PAGINATED_ERROR,
      payload: { message: 'Could not load the invoices. Please try again.' },
    });
  }
}

/**
 * Paginated invoices fetch - loads only pageSize items at a time
 * Uses cursor-based pagination with cached cursors for efficient navigation
 */
export function* getInvoicesPaginated({ payload }) {
  try {
    const { filter, page = 1, pageSize = 50, lastDoc = null } = payload;

    // Searching by number returns every match at once (a lookup, not browsing):
    // several prefix queries are merged, so cursor pagination does not apply.
    if (getInvoiceNumberTerm(filter)) {
      const found = yield call(searchInvoicesByNumber, filter, pageSize);
      yield put({
        type: actions.GET_INVOICES_PAGINATED_SUCCESS,
        invoices: found,
        hasMore: false,
        currentPage: 1,
        lastDoc: null,
        firstDoc: null,
      });
      return;
    }

    let filters = buildInvoiceFilters(filter);
    let orderBy = buildInvoiceOrderBy(filter);

    // Get paginated data using cursor (no count query for performance)
    const result = yield paginatedQueryFirestoreWithFilter(
      getAccountIdPrefix() + COLLECTION_NAME,
      filters,
      orderBy,
      pageSize,
      lastDoc // Cursor from previous page
    );
    
    const dataSource = [];
    Object.keys(result.data).map((item, index) => {
      return dataSource.push({
        ...result.data[item],
        key: item,
      });
    });
    
    yield put({
      type: actions.GET_INVOICES_PAGINATED_SUCCESS,
      invoices: dataSource,
      hasMore: result.hasMore,
      currentPage: page,
      lastDoc: result.lastDoc,
      firstDoc: result.firstDoc
    });
  } catch (error) {
    // NUNCA callar: si la consulta falla (indice ausente, permisos, red) y no se
    // despacha nada, el store conserva la pagina anterior y el usuario cree que
    // su busqueda/filtro se aplico. Paso en produccion al publicar el buscador.
    console.log(error);
    const searching = !!getInvoiceNumberTerm(payload && payload.filter);
    yield put({
      type: actions.GET_INVOICES_PAGINATED_ERROR,
      payload: {
        message: searching
          ? 'The search could not be completed. No results are being shown.'
          : 'Could not load the invoices. Please try again.',
      },
    });
  }
}

// The invoice as it is STORED right now (null if it no longer exists). The copy in memory can be a tab
// opened before somebody generated the invoice, or a form the user is still editing: this read is
// what decides whether a write may go ahead.
function* readStoredInvoice(key) {
  const snapshot = yield call(rsf.firestore.getDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${key}`);
  return snapshot && snapshot.exists ? snapshot.data() : null;
}

// What the browser writes: the invoice without its key, and the VERI*FACTU summary only as it was READ
// (the server owns it: a stale copy in memory must not revert it, and the browser must not forge one).
function dataToWrite(invoice, stored) {
  const data = omit(invoice, ['key', 'verifactu']);
  if (stored && stored.verifactu !== undefined) data.verifactu = stored.verifactu;
  return data;
}

const DOCUMENT_GONE_MESSAGE = 'This invoice no longer exists. Reload the list and try again.';

export function* updateInvoiceSaga({ payload }) {
  const {invoice, actionName, filter } = payload;
  try {
    switch (actionName) {
      case 'delete': {
        // A registered invoice (VERI*FACTU) is part of the AEAT chain: it can only be
        // cancelled from the VERI*FACTU page or rectified with a credit note.
        if (isRegistered(invoice)) {
          yield put({
            type: actions.DELETE_INVOICE_ERROR,
            payload: { message: REGISTERED_DELETE_MESSAGE },
          });
          return;
        }
        // A generated invoice is never deleted: it is corrected with a credit note. The STORED document
        // decides, not the copy in the list (which may be older than the moment somebody generated it).
        const storedToDelete = yield* readStoredInvoice(invoice.key);
        if (!storedToDelete) {
          yield put({ type: actions.DELETE_INVOICE_ERROR, payload: { message: DOCUMENT_GONE_MESSAGE } });
          return;
        }
        if (!isStoredDocEditable(storedToDelete)) {
          yield put({ type: actions.DELETE_INVOICE_ERROR, payload: { message: FINAL_DELETE_MESSAGE } });
          return;
        }
        // BR1: A DRAFT invoice with booking-linked items cannot be deleted manually.
        // For drafts: the booking is the source of truth; items are removed via
        // the UPDATE_INVOICE_ITEM_BOOKING flow when the booking is modified/deleted.
        const hasBookingItems = !!(invoice.invoiceList &&
          invoice.invoiceList.some(item => item.bookingId));
        if (hasBookingItems && invoice.orderStatus === 'draft') {
          yield put({
            type: actions.DELETE_INVOICE_ERROR,
            payload: { message: 'This invoice has items linked to a booking and cannot be deleted. Remove or modify the booking items first.' },
          });
          return;
        }
        yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoice.key}`, {
          ...dataToWrite(invoice, storedToDelete), //soft deletion
          deleted_at: new Date().getTime(),
        });
        // NOTE: We do NOT re-dispatch UPDATE_INVOICE_ITEM_BOOKING here.
        // The booking items remain unchanged in the booking document.
        // Draft items: next booking save will automatically re-create the draft.
        let filters = [{ field: 'invoice', operator: '==', value : invoice.id }];
        let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + 'payments', filters);
        if (data && Object.keys(data).length > 0){
          let paymentId = Object.keys(data);
          yield call(rsf.firestore.setDocument, getAccountIdPrefix() + `payments/${paymentId[0]}`, {
            ...data[paymentId[0]],
            deleted_at: new Date().getTime(),
          });
        }
        yield put({
          type: actions.DELETE_INVOICE_SUCCESS,
          payload: { message: '1 ' + invoice.type + ' deleted' },
        });
        break;
      }
      case 'update': {
        // Only a draft (or a note, or a final invoice VERI*FACTU blocked before the AEAT ever saw it, which the
        // office has to fix) may be overwritten. Anything else is refused here, whatever the page showed.
        const storedToUpdate = yield* readStoredInvoice(invoice.key);
        if (!storedToUpdate) {
          yield put({ type: actions.UPDATE_INVOICE_ERROR, payload: { message: DOCUMENT_GONE_MESSAGE } });
          return;
        }
        if (!isStoredDocEditable(storedToUpdate) && !isBlockedForFixing(storedToUpdate)) {
          yield put({
            type: actions.UPDATE_INVOICE_ERROR,
            payload: { message: FINAL_LOCK_MESSAGE, storedOrderStatus: storedToUpdate.orderStatus },
          });
          return;
        }
        yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoice.key}`, dataToWrite(invoice, storedToUpdate));
        break;
      }
      default:
        yield call(rsf.firestore.addDocument, getAccountIdPrefix() + COLLECTION_NAME, invoice);
        break;
    }
    yield put({ type: actions.UPDATE_INVOICE_SUCCESS });
    // Refresh with pagination after invoice update
    yield put({ type: actions.GET_INVOICES_PAGINATED, payload: { filter, page: 1, pageSize: 50 }});
  } catch (error) {
    // This used to be a console.log only: a refused save left the user looking at a form that said nothing.
    console.log(error);
    const isDelete = actionName === 'delete';
    yield put({
      type: isDelete ? actions.DELETE_INVOICE_ERROR : actions.UPDATE_INVOICE_ERROR,
      payload: {
        message: invoiceErrorMessage(
          error,
          isDelete ? 'The invoice could not be deleted. Please try again.' : 'The invoice could not be saved. Please try again.'
        ),
      },
    });
  }
}

// The e-mail went out (or failed): the only thing the browser may still write on a generated invoice.
// A partial update with ONLY the delivery flags, built from the keys that are defined (Firestore rejects
// undefined): a whole-document write from a copy read earlier could overwrite anything else, including
// the VERI*FACTU summary the server keeps up to date.
export function* markInvoiceEmailStatus({ payload }) {
  const { key, emailSent, emailSentLog, filter } = payload;
  const data = {};
  if (emailSent !== undefined) data.emailSent = !!emailSent;
  if (emailSentLog !== undefined) data.emailSentLog = emailSentLog;
  if (!key || Object.keys(data).length === 0) return;
  try {
    yield call(rsf.firestore.updateDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${key}`, data);
    yield put({ type: actions.GET_INVOICES_PAGINATED, payload: { filter, page: 1, pageSize: 50 }});
  } catch (error) {
    console.log(error);
    yield put({
      type: actions.UPDATE_INVOICE_ERROR,
      payload: { message: invoiceErrorMessage(error, 'The e-mail status of the invoice could not be saved.') },
    });
  }
}

export function* setPaymentInvoice(invoice) {
  if (invoice.invoice_serie === '7' || invoice.invoice_serie === '8') return; //fix to exclude rentals invoices from payments
  let filters = [{field: 'invoice', operator: '==', value : invoice.id}];
  let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + 'payments', filters);
  if (Object.keys(data).length > 0 ){
    let paymentId = Object.keys(data);
    yield call(rsf.firestore.setDocument, getAccountIdPrefix() + `payments/${paymentId[0]}`, {
      ...omit(data[paymentId[0]], ['description']),
      debit: invoice.totalCost,
      type:invoice.type,
      description: invoice.type +" #" + invoice.number,
    });
  }else{
    const payment = {
      credit: "0.00",
      debit: invoice.totalCost,
      date: invoice.orderDate,
      deleted_at: null,
      description: invoice.type + " #" + invoice.number,
      invoice: invoice.id,
      type: invoice.type,
      billToId: invoice.billToId,
      idOwner: invoice.billToId,//fix replaced idOwner
      createdAt: new Date().getTime(),
      id: 1,
    }
    yield call(rsf.firestore.addDocument, getAccountIdPrefix() + `payments`, payment);
  }
}

export function* updateInvoicesBulk({ payload }) {
  const getYearFromTimestamp = (d) => {
    let date = new Date(d);
    return date.getFullYear().toString();
  }
  const { invoices, series, filter } = payload;
  const problems = [];
  const skipped = [];
  try {
    // Sort ASC by orderDate so earlier invoices get lower sequential numbers
    const sortedInvoices = [...invoices].sort((a, b) => a.orderDate - b.orderDate);
    let objectSeries = Object.keys(series);
    for (let i = 0; objectSeries.length > i ; i++){
      const serieKey = objectSeries[i];
      for (let y = 0; sortedInvoices.length > y; y++){
        const candidate = sortedInvoices[y];
        const serieId = (!candidate.invoice_serie || candidate.invoice_serie === "0")
          ? ((candidate.type === "invoice") ? "1" : "101")
          : candidate.invoice_serie;
        if (String(series[serieKey].id) === serieId && candidate.orderStatus === 'draft' ){
          // The list may be older than the moment somebody generated this one: only a draft that is STILL a
          // draft in the database is generated (a second pass over a final one would rewrite it).
          const stored = yield* readStoredInvoice(candidate.key);
          if (!stored || stored.orderStatus !== 'draft') {
            skipped.push(candidate.number || candidate.id);
            continue;
          }
          const year = getYearFromTimestamp(candidate.orderDate);
          const years = series[serieKey].years;
          const next = ((years !== undefined && years[year] !== undefined) ? Number(years[year]) : 0) + 1;
          const generated = {
            ...candidate,
            invoice_serie: serieId,
            orderStatus: candidate.type,
            number: String(series[serieKey].id) + "/" + String(next).padStart(4, "0"),
          };
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${candidate.key}`, {
            ...omit(generated, ['key', 'verifactu'])
          });
          // The number is consumed only now that the invoice exists: a denied write never leaves a gap.
          // (The counter lives in Redux and is shared by the whole run.)
          if (series[serieKey].years === undefined) series[serieKey].years = {};
          series[serieKey].years[year] = next;
          yield put({type: actionSerie.SAVE_INTO_FIRESTORE, payload: { data: { key: serieKey, ...series[serieKey] }, actionName: 'update' }});
          yield setPaymentInvoice(generated);
        }
      }
    }
  } catch (error) {
    console.log(error);
    problems.push(invoiceErrorMessage(error, 'Some invoices could not be generated. Reload the list and check them before trying again.'));
  }
  if (skipped.length > 0) {
    problems.unshift(
      `${skipped.length} invoice${skipped.length === 1 ? ' was' : 's were'} not generated because ${skipped.length === 1 ? 'it is' : 'they are'} no longer ${skipped.length === 1 ? 'a draft' : 'drafts'}: ${skipped.join(', ')}. Reload the list.`
    );
  }
  if (problems.length > 0) {
    yield put({ type: actions.UPDATE_INVOICE_ERROR, payload: { message: problems.join(' ') } });
  }
  yield put({ type: actions.UPDATE_INVOICES_BULK_SUCCESS, payload: {}});
  // Refresh with pagination after bulk update
  yield put({ type: actions.GET_INVOICES_PAGINATED, payload: { filter, page: 1, pageSize: 50 }});
}

export function* fixPaymentsInvoicesBulk(){
  let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, []);
  const dataSource = [];
  Object.keys(data).map((item, index) => {
    return dataSource.push({
      ...data[item],
      key: item,
    });
  });
  for (let i = 0; i < dataSource.length; i++){
    if (dataSource[i].id && (dataSource[i].invoice_serie !=='7' && dataSource[i].invoice_serie !=='8')){
      let filters = [{field: 'invoice', operator: '==', value : dataSource[i].id}];
      let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + 'payments', filters);
      if (data && Object.keys(data).length !== 0){
        let paymentId = Object.keys(data);
        yield call(rsf.firestore.setDocument, getAccountIdPrefix() + `payments/${paymentId[0]}`, {
          ...data[paymentId[0]],
          type: dataSource[i].type,
        });
        console.log('updated payment '+paymentId[0])
      }
    }
  }
  console.log('end');
}

export function* updateDataUserInvoice({payload}) {
  console.log(payload);
  try {
    let filters = [];
    filters.push({field: 'billToId', operator: '==', value : payload.dataUser.id});
    filters.push({field: 'orderStatus', operator: '==', value : 'draft'});
    let data = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix()+ COLLECTION_NAME, filters);
    const dataSource = [];
    for (const id in data) {
        if (Object.prototype.hasOwnProperty.call(data, id)) {
          //I MODIFY THE INVOICE VALUES
          const item = data[id];
          item.billTo = payload.dataUser.business_name;
          item.cifTo = payload.dataUser.cif;
          item.billToAddress = payload.dataUser.address + " - " + payload.dataUser.address;
          item.companyTo = payload.dataUser.business_name;
          dataSource.push({
            ...item,
            key: id,
          });
        }
     }
    ///////////////////
    const fieldsToOmit = ['key'];
    for (let i = 0; i < dataSource.length; i++){
      yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${dataSource[i].key}`, {
        ...omit(dataSource[i], fieldsToOmit),
      });
    }
  } catch (error) {
    // Runs from the client page, with no invoice list on screen: say it where the user is looking.
    console.log(error);
    yield call(notifyInvoiceError, invoiceErrorMessage(error, 'The client data could not be copied to the draft invoices.'));
  }
  return yield false;
}

export function* updateInvoiceItemBooking({payload}) {
  const { propertyId, bookingId, items } = payload;

  const setNewInvoice = (item, client, property ,bookingId) => {
    let itemDate = new Date(item.createdAt);
    let itemType = (item.invoice) ? 'invoice': 'note';
    let invoice = clone(newInvoice);
    const newId = generateSystemId();
    invoice.id = newId;
    invoice.number = `#${newId}`;
    invoice.orderStatus = 'draft';
    invoice.orderDate = DateFormatter.getLastDayMonthValue(itemDate);
    invoice.orderMonth = itemDate.getMonth();
    invoice.billTo = client.business_name;
    invoice.billToId = property[0].ownerId;
    invoice.ownerId = property[0].ownerId;
    invoice.billToAddress = `${client.address} ${client.city ? "- " + client.city : ""} ${client.state ? "- " + client.state : ""}`;
    invoice.cifTo = client.cif;
    invoice.companyTo = client.business_name;
    invoice.invoice_serie = (itemType === 'invoice') ? (client.invoice_serie).toString() : noteSeries.normal.id.toString();
    invoice.invoiceProperty = property[0].id;
    invoice.propertyName = property[0].name;
    invoice.invoiceList.pop();
    invoice.vatRate = (item.invoice) ? 21 : 0;
    invoice.type = itemType;
    invoice.invoiceList = getItems(invoice.invoiceList, item, property, bookingId);
    return updateValues(invoice);
  }

  const updateInvoice = (item, invoice, property, bookingId) => {
    invoice.invoiceList = getItems(invoice.invoiceList, item, property, bookingId);
    // Bug fixed 2026-09-18: orderDate/orderMonth used to be set only once, at
    // creation (setNewInvoice below). If the source item's date was later
    // corrected in the booking (a wrong year, a moved date), the header stayed
    // frozen on the stale value forever - the invoice silently drifted a full
    // year away from its own line items. Recomputing it here from the item
    // that is actually being written keeps the header self-healing on every
    // sync, instead of trusting a snapshot taken who-knows-when.
    let itemDate = new Date(item.createdAt);
    invoice.orderDate = DateFormatter.getLastDayMonthValue(itemDate);
    invoice.orderMonth = itemDate.getMonth();
    return updateValues(invoice);
  }

  function* getPropertyFromBooking (propertyId) {
    let filtersProp = [];
    filtersProp.push({ field: 'id', operator: '==', value : propertyId });
    let dataProperty = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME_PROP, filtersProp);
    let property = Object.keys(dataProperty).map((i, index) => {
      return {
        ...dataProperty[i],
        key: i,
      };
    });
    return yield property;
  }

  function* getClientFromProperty (property) {
    let filtersClient = [];
    filtersClient.push({ field: 'id', operator: '==', value : property[0].ownerId });
    let dataClient = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME_CLIENT, filtersClient);
    let client = Object.keys(dataClient).map((i, index) => {
      return {
        ...dataClient[i],
        key: i,
      };
    });
    return yield client;
  }

  function* getInvoiceFromBookingItem (item, property, client) {
    let filters = [];
    let itemDate = new Date(item.createdAt);
    let itemType = (item.invoice) ? 'invoice': 'note';
    let invoiceSerie = (itemType === 'invoice') ? (client.invoice_serie).toString() : noteSeries.normal.id.toString();
    // Use the first and last day of the item's month to isolate by year + month
    // (orderMonth alone matches the same calendar month across all years)
    const firstDayOfMonth = moment(new Date(itemDate.getFullYear(), itemDate.getMonth(), 1, 0, 0)).valueOf();
    const lastDayOfMonth = DateFormatter.getLastDayMonthValue(itemDate);
    filters.push({field: 'orderDate', operator: '>=', value : firstDayOfMonth});
    filters.push({field: 'orderDate', operator: '<=', value : lastDayOfMonth});
    filters.push({field: 'orderMonth', operator: '==', value : itemDate.getMonth()});
    filters.push({field: 'billToId', operator: '==', value : property[0].ownerId });
    filters.push({field: 'type', operator: '==', value : itemType });
    filters.push({field: 'invoiceProperty', operator: '==', value : property[0].id });
    filters.push({field: 'invoice_serie', operator: '==', value : invoiceSerie });
    filters.push({field: 'orderStatus', operator: '==', value : 'draft' });
    filters.push({field: 'deleted_at', operator: '==', value : null });
    return yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, filters);
  }

  // Helper function to find all invoices that contain items with a specific bookingId
  function* getInvoicesFromBookingId(bookingId, property, client) {
    let filters = [];
    filters.push({field: 'billToId', operator: '==', value : property[0].ownerId });
    filters.push({field: 'invoiceProperty', operator: '==', value : property[0].id });
    filters.push({field: 'orderStatus', operator: '==', value : 'draft' });
    filters.push({field: 'deleted_at', operator: '==', value : null });
    let allInvoices = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, filters);
    
    // Filter invoices that contain items from this booking
    let invoicesWithBookingItems = {};
    Object.keys(allInvoices).forEach(invoiceKey => {
      const invoice = allInvoices[invoiceKey];
      const hasBookingItems = invoice.invoiceList && invoice.invoiceList.some(item => item.bookingId === bookingId);
      if (hasBookingItems) {
        invoicesWithBookingItems[invoiceKey] = invoice;
      }
    });
    
    return invoicesWithBookingItems;
  }

  const getItems = (itemList , item, property, bookingId) => {
    let cont = 1;
    let itemsAux = itemList
    .filter((i, index) => {
      if ((i.itemId === item.itemId )){
        return false;
      }
      return true;
    })
    .map( (i, index) => {
      i.key = cont;
      cont++;
      return i;
    });

    if (!item.deleted) {
      itemsAux.push({
        key: cont,
        itemName: item.itemName,
        itemType: 'T',
        noTaxable: (item.invoice) ? false : true,
        costs: item.costs,
        qty: item.qty,
        price: item.price,
        propertyId: property[0].id,
        bookingId: bookingId,
        itemId: item.itemId,
        createdAt: item.createdAt
      });
    }
    return itemsAux.sort((a, b) => {
      return a.createdAt - b.createdAt;
    }).map( (i, index) => {
      i.key = index+1;
      return i;
    });
  }

  // Helper: load all finalized (non-draft, non-deleted) invoices for this property/owner
  // Used to detect if a booking item is already billed and prevent duplicate drafts
  function* getFinalizedInvoicesForProperty(property) {
    let filters = [];
    filters.push({field: 'billToId', operator: '==', value : property[0].ownerId });
    filters.push({field: 'invoiceProperty', operator: '==', value : property[0].id });
    filters.push({field: 'deleted_at', operator: '==', value : null });
    const all = yield simpleQueryFirestoreWithFilter(getAccountIdPrefix() + COLLECTION_NAME, filters);
    const finalized = {};
    Object.keys(all).forEach(key => {
      if (all[key].orderStatus !== 'draft') finalized[key] = all[key];
    });
    return finalized;
  }

  // Pure check: is this booking item already present in a finalized invoice?
  const isItemAlreadyFinalized = (item, bookingId, finalizedInvoices) =>
    Object.values(finalizedInvoices).some(inv =>
      inv.invoiceList && inv.invoiceList.some(
        line => line.itemId === item.itemId && line.bookingId === bookingId
      )
    );

  let property = yield getPropertyFromBooking(propertyId);
  let client = yield getClientFromProperty(property);

  try {
    // STEP 1: First, clean up deleted items from all invoices related to this booking
    // This handles the case when items are removed or the booking date changes
    let existingInvoices = yield getInvoicesFromBookingId(bookingId, property, client[0]);
    const deletedItems = items.filter(item => item.deleted === true);
    
    for (const invoiceKey in existingInvoices) {
      if (Object.prototype.hasOwnProperty.call(existingInvoices, invoiceKey)) {
        const invoice = existingInvoices[invoiceKey];
        let hasChanges = false;
        
        // Remove deleted items from this invoice
        deletedItems.forEach(deletedItem => {
          const initialLength = invoice.invoiceList.length;
          invoice.invoiceList = invoice.invoiceList.filter(invItem => !(invItem.itemId === deletedItem.itemId && invItem.bookingId === bookingId));
          if (initialLength !== invoice.invoiceList.length) {
            hasChanges = true;
          }
        });
        
        // If invoice is now empty, mark it as deleted
        if (invoice.invoiceList.length === 0) {
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoiceKey}`, {
            deleted_at: new Date().getTime(),
          });
          yield put({ type: actionsLog.SAVE_INTO_FIRESTORE, payload: { data: {
            id: generateSystemId(),
            user_id: null,
            text: 'Draft invoice #'+invoice.id+' deleted: no items remaining from booking',
            description: 'Draft cleanup',
            module: 'booking',
            type: 'info',
            checked: false,
            created_at: new Date().getTime(),
            deleted_at: null,
          }}});
        } else if (hasChanges) {
          // Update invoice with recalculated totals
          const updatedInvoice = updateValues(invoice);
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoiceKey}`, {
            ...omit(updatedInvoice, ['key']),
          });
        }
      }
    }

    // STEP 2: Process new/updated items and create or update invoices
    // Load finalized invoices ONCE — used to skip items already billed
    const finalizedInvoices = yield getFinalizedInvoicesForProperty(property);
    const activeItems = items.filter(item => item.deleted !== true);

    // Session-local draft map: prevents race conditions within the loop.
    // When a booking has multiple items in the same month (e.g. arrival + departure + cleaning),
    // the loop runs sequentially. Without this map, each iteration would call
    // getInvoiceFromBookingItem and NOT see the draft created by the previous iteration
    // (because the prior `put`-based insert was async and not awaited).
    // With this map, the second+ items reuse the in-memory draft instead of querying Firestore,
    // ensuring they ALL end up in the SAME grouped draft instead of creating one draft per item.
    // Key format: billToId_propertyId_year_month_type_serie
    const sessionDraftMap = {};

    const getDraftKey = (item) => {
      const d = new Date(item.createdAt);
      const t = item.invoice ? 'invoice' : 'note';
      const s = item.invoice ? client[0].invoice_serie.toString() : noteSeries.normal.id.toString();
      return `${property[0].ownerId}_${property[0].id}_${d.getFullYear()}_${d.getMonth()}_${t}_${s}`;
    };

    for (let i = 0; i < activeItems.length; i++ ){
      let item = activeItems[i];

      // Skip items already present in a finalized invoice — prevents duplicate billing
      if (isItemAlreadyFinalized(item, bookingId, finalizedInvoices)) {
        continue;
      }

      const draftKey = getDraftKey(item);
      let existingDraft = null;
      let existingDocKey = null;

      if (sessionDraftMap[draftKey]) {
        // Reuse in-memory draft — avoids race condition on same-month items
        existingDraft = sessionDraftMap[draftKey].invoiceObj;
        existingDocKey = sessionDraftMap[draftKey].docKey;
      } else {
        // First time we see this month/type combination: query Firestore
        const invoicesFromDB = yield getInvoiceFromBookingItem(item, property, client[0]);
        const keys = Object.keys(invoicesFromDB);
        if (keys.length > 0) {
          existingDraft = invoicesFromDB[keys[0]];
          existingDocKey = keys[0];
        }
      }

      if (!existingDraft) {
        // No draft exists — create one with a direct call (awaited) so the next
        // loop iteration can see it via sessionDraftMap without hitting Firestore again
        let newInv = setNewInvoice(item, client[0], property, bookingId);
        const docRef = yield call(rsf.firestore.addDocument, getAccountIdPrefix() + COLLECTION_NAME, omit(newInv, ['key']));
        newInv.key = docRef.id;
        sessionDraftMap[draftKey] = { invoiceObj: newInv, docKey: docRef.id };
        yield setPaymentInvoice(newInv);
        yield put({ type: actionsLog.SAVE_INTO_FIRESTORE, payload: { data: {
              id: generateSystemId(),
              user_id: null,
              text: 'item '+item.itemName+' has created a new invoice #'+newInv.id,
              description: 'New Invoice from booking',
              module: 'booking',
              type: 'success',
              checked: false,
              created_at: new Date().getTime(),
              deleted_at: null,
              }}});
      } else {
        // Draft exists — add the item to it and write synchronously
        let invoice = updateInvoice(item, { ...existingDraft, key: existingDocKey }, property, bookingId);
        invoice.key = existingDocKey;
        sessionDraftMap[draftKey] = { invoiceObj: invoice, docKey: existingDocKey };
        yield setPaymentInvoice(invoice);
        if (invoice.totalCost === 0) {
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoice.key}`, {
            deleted_at: new Date().getTime(),
          });
          sessionDraftMap[draftKey] = null; // draft is gone
        } else {
          yield call(rsf.firestore.setDocument, `${getAccountIdPrefix() + COLLECTION_NAME}/${invoice.key}`, {
            ...omit(invoice, ['key']),
          });
        }
      }
    }
  } catch(error){
      // Runs from the booking pages, with no invoice list on screen: say it where the user is looking.
      console.log(error);
      yield call(notifyInvoiceError, invoiceErrorMessage(error, 'The invoice lines of this booking could not be updated. Check the draft invoices before continuing.'));
  }
  yield put({ type: actions.UPDATE_INVOICE_ITEM_BOOKING_SUCCESS, payload: {}});
  return true;
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.GET_INVOICE, getInvoice),
    takeLatest(actions.GET_INVOICES, getInvoices),
    takeLatest(actions.GET_INVOICES_PAGINATED, getInvoicesPaginated),
    takeEvery(actions.UPDATE_INVOICE, updateInvoiceSaga),
    takeEvery(actions.MARK_INVOICE_EMAIL_STATUS, markInvoiceEmailStatus),
    takeEvery(actions.UPDATE_INVOICE_ITEM_BOOKING, updateInvoiceItemBooking),
    takeEvery(actions.UPDATE_INVOICES_BULK, updateInvoicesBulk),
    takeEvery(actions.FIX_PAYMENTS_INVOICES_BULK, fixPaymentsInvoicesBulk),
    takeEvery(actions.UPDATE_DATA_USER_INVOICE, updateDataUserInvoice),
    //takeEvery(actions.FIX_PAYMENTS_INVOICE, deleteUpdatePaymentFix)
  ]);
}
